import { apiService } from './apiService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.eternal-health.com/api';

export interface MedicalReport {
  id: number;
  fileUrl: string;
  reportType: string;
  subType: string;
  description: string;
  uploadedAt: string;
  patientId: number;
  patientName: string;
}

export interface MedicalReportsResponse {
  patientId: number;
  patientName: string;
  totalReports: number;
  reports: MedicalReport[];
}

export interface UploadReportData {
  file: File;
  report_type: string;
  sub_type: string;
  description: string;
}

const getAuthToken = () => {
  return localStorage.getItem('access');
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

// Helper function to handle API responses with 401 detection
const handleResponse = async (response: Response) => {
  if (response.status === 401) {
    // Clear all auth data from localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    
    // Redirect to login page
    window.location.href = '/login';
    throw new Error('Authentication expired. Please login again.');
  }
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

export const documentService = {
  // GET /api/clinic/patient/{patient_id}/medical-reports/
  getMedicalReports: async (patientId: string | number): Promise<MedicalReportsResponse> => {
    const url = `${API_BASE_URL}/clinic/patient/${patientId}/medical-reports/`;
    try {
      const response = await apiService.get(url);
      return response;
    } catch (error) {
      console.error(`Error fetching medical reports for patient ${patientId}:`, error);
      throw error;
    }
  },

  // POST /api/clinic/patient/{patient_id}/medical-reports/upload/
  uploadMedicalReport: async (patientId: string | number, data: UploadReportData): Promise<MedicalReport> => {
    const url = `${API_BASE_URL}/clinic/patient/${patientId}/medical-reports/upload/`;
    
    try {
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('report_type', data.report_type);
      formData.append('sub_type', data.sub_type);
      formData.append('description', data.description);

      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(), // Don't include Content-Type for FormData
        body: formData,
      });

      return handleResponse(response);
    } catch (error) {
      console.error(`Error uploading medical report for patient ${patientId}:`, error);
      throw error;
    }
  },

  // DELETE /api/clinic/patient/{patient_id}/medical-reports/{report_id}/
  deleteMedicalReport: async (patientId: string | number, reportId: string | number): Promise<void> => {
    const url = `${API_BASE_URL}/clinic/patient/${patientId}/medical-reports/${reportId}/`;
    
    try {
      await apiService.delete(url);
    } catch (error) {
      console.error(`Error deleting medical report ${reportId} for patient ${patientId}:`, error);
      throw error;
    }
  },
};
